import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";

import {
  Divider as MuiDivider,
  Grid,
  Typography as MuiTypography,
  Button as MuiButton,
  IconButton as MuiIconButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButton as MuiToggleButton,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import { spacing, SpacingProps } from "@mui/system";
import {
  setQueryLoadingState,
  setSearchQuery,
  setClusters,
  setQueryResult,
} from "../../redux/slices/search";
import { RootState } from "../../redux/store";

import {
  FilterList as FilterListIcon,
  TableRows as TableIcon,
  Forest as TreeMapIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Delete as DeleteIcon,
  Bookmarks as BookmarkIcon,
} from "@mui/icons-material";
import NoResultsFound from "../../components/pages/NoResultsFound";
import QueryResultCard from "../../components/pages/search/QueryResultCard";
import Loader from "../../components/Loader";
import FilterDialog from "../../components/pages/search/FilterDialog";
import useAppSelector from "../../hooks/useAppSelector";
import Treemap from "../../components/pages/search/Treemap";
import TreemapList from "../../components/pages/search/TreemapList";
import useAppDispatch from "../../hooks/useAppDispatch";
import { deleteQueryFromDB } from "../../firebase/utils";
import { Query, QueryResult } from "../../types/app";
import {
  setBookmarkDialogState,
  setPreUploadedBookmarks,
} from "../../redux/slices/bookmark";

const Divider = styled(MuiDivider)(spacing);

const IconButton = styled(MuiIconButton)`
  height: 40px;
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
`;

const QueryContainer = styled.div``;

const SubQueryContainer = styled.div`
  display: flex;
`;

const DropDownButton = styled(MuiButton)`
  margin-top: 4px;
  padding: 0px;
  &:hover {
    background-color: transparent;
  }
`;

const UtilsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MainContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const TreemapContainer = styled.div`
  width: 100%;
  height: calc(100vh - ${250}px);
  display: flex;
`;

const ToggleButtonGroup = styled(MuiToggleButtonGroup)`
  margin-right: 10px;
`;

const ToggleButton = styled(MuiToggleButton)`
  height: 35px;
  width: 35px;

  &.MuiToggleButton-root.Mui-selected {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: white;
  }
`;

const Menu = styled(MuiMenu)`
  margin-top: 8px;

  .MuiMenu-paper {
    padding: 5px 5px 2px 10px;
    border-radius: 10px;
    box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.4);
    max-height: 300px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MenuItem = styled(MuiMenuItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 10px;
`;

const DeleteButton = styled(MuiIconButton)`
  height: 25px;
  width: 25px;
  margin-left: 10px;
`;

const MenuItemText = styled(MuiTypography)`
  width: 200px;
  padding: 5px 0px;
  text-overflow: ellipsis;
`;

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

function Search() {
  const state = useAppSelector((state: RootState) => state.main);
  const searchState = useAppSelector((state: RootState) => state.search);
  const dispatch = useAppDispatch();
  const queryResult = searchState.queryResult;
  const dirSearchQuery = searchState.searchQuery;

  const [open, setOpen] = useState(false);
  const [clustersDisplay, setClustersDisplay] = useState<Set<string>>(
    new Set()
  );
  const [pdfDisplay, setPdfDisplay] = useState<Set<string>>(new Set());
  const [orderBy, setOrderBy] = useState("Confidence");
  const [displayMethod, setDisplayMethod] = useState("table");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleHistoryClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClicked = (query: Query) => {
    setAnchorEl(null);
    dispatch(setSearchQuery(query.query));
    dispatch(setClusters(query.clusters));
    dispatch(setQueryResult(query.queryResults));
    dispatch(setPreUploadedBookmarks([]));
  };

  const handleDeleteQuery = async (query: Query) => {
    dispatch(setQueryLoadingState(true));
    await deleteQueryFromDB(query);
    dispatch(setQueryLoadingState(false));
  };

  useEffect(() => {
    if (searchState.queries !== undefined) {
      setClustersDisplay(new Set(searchState.clusters));
      setPdfDisplay(new Set(state.files.map((file) => file.fileName)));
    }
  }, [searchState.searchQuery]);

  return (
    <React.Fragment>
      {searchState.queryLoading || state.fetchHistoricalQueryLoading ? (
        <Loader />
      ) : queryResult.length === 0 ? (
        <NoResultsFound />
      ) : (
        <MainContainer>
          <TopSection>
            <QueryContainer>
              <Typography variant="h3" gutterBottom display="inline">
                Query
              </Typography>
              <SubQueryContainer>
                <DropDownButton onClick={handleHistoryClick}>
                  <Typography color="textPrimary">{dirSearchQuery}</Typography>
                  <ArrowDropDownIcon sx={{ color: "black" }} />
                </DropDownButton>
              </SubQueryContainer>
            </QueryContainer>
            <UtilsContainer>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
              >
                {searchState.queries.map((query, idx) => {
                  return (
                    <MenuItem
                      onClick={() => handleMenuClicked(query)}
                      key={idx}
                    >
                      <MenuItemText noWrap>{query.query}</MenuItemText>
                      <DeleteButton onClick={() => handleDeleteQuery(query)}>
                        <DeleteIcon sx={{ fontSize: "20px" }} />
                      </DeleteButton>
                    </MenuItem>
                  );
                })}
              </Menu>
              <ToggleButtonGroup
                value={displayMethod}
                exclusive
                onChange={(e, value) => setDisplayMethod(value)}
              >
                <ToggleButton value="table">
                  <TableIcon sx={{ fontSize: "17px" }} />
                </ToggleButton>
                <ToggleButton value="treemap">
                  <TreeMapIcon sx={{ fontSize: "17px" }} />
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton
                onClick={() => dispatch(setBookmarkDialogState(true))}
              >
                <BookmarkIcon sx={{ fontSize: "19px" }} />
              </IconButton>
              <IconButton onClick={() => setOpen(true)}>
                <FilterListIcon />
              </IconButton>
            </UtilsContainer>
          </TopSection>

          <Divider my={6} />

          {displayMethod === "table" ? (
            <Grid container spacing={6} paddingBottom="15px">
              {queryResult
                .filter((element: QueryResult) =>
                  clustersDisplay.has(searchState.clusters[element.cluster])
                )
                .filter((element: QueryResult) =>
                  pdfDisplay.has(element.pdfName)
                )
                .sort((a: QueryResult, b: QueryResult) => {
                  return orderBy === "Cluster"
                    ? a.cluster - b.cluster
                    : b.score - a.score;
                })
                .map((item: QueryResult) => {
                  return (
                    <Grid item xs={12} lg={6} xl={4} key={item.rank}>
                      <QueryResultCard
                        rank={item.rank}
                        description={item.description}
                        summary={item.summary}
                        answer={item.answer}
                        pdfName={item.pdfName}
                        page={item.page}
                        score={item.score}
                        cluster={item.cluster}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          ) : (
            <TreemapContainer>
              <Treemap />
              <TreemapList />
            </TreemapContainer>
          )}
          <FilterDialog
            open={open}
            setOpen={setOpen}
            clusterDisplay={clustersDisplay}
            setClusterDisplay={setClustersDisplay}
            pdfDisplay={pdfDisplay}
            setPdfDisplay={setPdfDisplay}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
          />
        </MainContainer>
      )}
    </React.Fragment>
  );
}

export default Search;
